var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { InteractionsServiceConstants, SERVICE } from "@agentx/agentx-services";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { logger } from "../logger/store-logger";
import { TasksType } from "./tasks-store.interface";
import { mapContactToTask, mapTaskDetailsToTask } from "./tasks-store.utils";
export class TasksStore {
    constructor(userStore) {
        this.privateSelectedTask = undefined;
        //Using @observable.ref as we only care if the map itself has changed, individual updates are covered in AGGrid with .observe()
        this.openTasks = observable.map();
        this.queuedTasks = observable.map();
        this.scheduledTasks = observable.map();
        this.closedTasks = observable.map();
        //TODO: Defined errors codes in the interface instead of booleans
        this.assignTaskError = false;
        this.queuedTasksError = false;
        this.userStore = userStore;
        makeObservable(this);
    }
    get selectedTask() {
        return this.privateSelectedTask;
    }
    get openTasksArray() {
        return Array.from(this.openTasks.values());
    }
    get openChatTaskCount() {
        return this.openTasksArray.filter((task) => task.mediaType === "chat").length;
    }
    get openEmailTaskCount() {
        return this.openTasksArray.filter((task) => task.mediaType === "email").length;
    }
    get openTelephonyTaskCount() {
        return this.openTasksArray.filter((task) => task.mediaType === "telephony").length;
    }
    get openSocialTaskCount() {
        return this.openTasksArray.filter((task) => task.mediaType === "social").length;
    }
    get remainingChatTaskCount() {
        return Math.max(this.userStore.channels.maChatCount - this.openChatTaskCount, 0);
    }
    get remainingEmailTaskCount() {
        return Math.max(this.userStore.channels.maEmailCount - this.openEmailTaskCount, 0);
    }
    get remainingSocialTaskCount() {
        return Math.max(this.userStore.channels.maSocialCount - this.openSocialTaskCount, 0);
    }
    getTasksByType(tasksType) {
        switch (tasksType) {
            case TasksType.Open:
                return this.openTasks;
            case TasksType.Queued:
                return this.queuedTasks;
            case TasksType.Scheduled:
                return this.scheduledTasks;
            case TasksType.Closed:
                return this.closedTasks;
            default:
                throw new Error("Invalid tasks type");
        }
    }
    pollForQueuedTasks(sortBy) {
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("[TasksStore] Starting polling for queued tasks, sortBy:", sortBy);
            if (this.queuedPollingSubscriptionId) {
                if (JSON.stringify(this.lastSortBy) === JSON.stringify(sortBy)) {
                    return;
                }
                else {
                    yield this.stopQueuedPolling();
                }
            }
            this.lastSortBy = sortBy;
            try {
                const { data } = yield SERVICE.cms.fetchCherryPickEnabledQueues({
                    orgId: this.userStore.orgId,
                    agentId: this.userStore.agentDbId,
                    teamId: this.userStore.teamId
                });
                const queueIdsFilters = data.map((queue) => {
                    const queueFilter = {
                        filterType: InteractionsServiceConstants.FilterType.StringFilter,
                        filter: queue.id,
                        operator: InteractionsServiceConstants.FilterOperator.Equals
                    };
                    return queueFilter;
                });
                const filters = {
                    [InteractionsServiceConstants.QueryField.QueueId]: queueIdsFilters
                };
                this.queuedPollingSubscriptionId = yield SERVICE.interactions.startQueuedPolling((taskDetails) => this.handleQueuedTasksCallback(taskDetails), sortBy, filters);
                runInAction(() => {
                    this.queuedTasksError = false;
                });
                logger.info("[TasksStore] Successfully started queued polling with subscription ID:", this.queuedPollingSubscriptionId);
            }
            catch (error) {
                runInAction(() => {
                    this.queuedTasksError = true;
                });
                logger.error("[TasksStore] Error starting queued polling:", error);
                throw error;
            }
        });
    }
    fetchMoreQueuedTasks() {
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("[TasksStore] Fetching next page of queued tasks:");
            if (this.queuedPollingSubscriptionId) {
                try {
                    const areMore = yield SERVICE.interactions.fetchMoreQueuedInteractions(this.queuedPollingSubscriptionId);
                    runInAction(() => {
                        this.queuedTasksError = false;
                    });
                    return areMore;
                }
                catch (error) {
                    runInAction(() => {
                        this.queuedTasksError = true;
                    });
                    logger.error("[TasksStore] Error fetching next page of queued tasks:", error);
                    throw error;
                }
            }
            return false;
        });
    }
    setSelectedTask(taskId) {
        if (taskId !== "") {
            const task = this.getTaskById(taskId);
            if (task) {
                this.privateSelectedTask = task;
            }
        }
        else {
            this.privateSelectedTask = undefined;
        }
    }
    getTaskById(taskId) {
        var _a, _b, _c;
        return ((_c = (_b = (_a = this.openTasks.get(taskId)) !== null && _a !== void 0 ? _a : this.queuedTasks.get(taskId)) !== null && _b !== void 0 ? _b : this.scheduledTasks.get(taskId)) !== null && _c !== void 0 ? _c : this.closedTasks.get(taskId));
    }
    assignTask(taskId) {
        return SERVICE.tasks
            .assignTask(taskId)
            .then((result) => {
            if (result.success) {
                logger.info("[TasksStore] Task assigned successfully:", taskId);
                this.moveTasksFromQueuedToOpen([result]);
                runInAction(() => {
                    this.assignTaskError = false;
                });
            }
            else {
                logger.error("[TasksStore] Error assigning task:", taskId, "Error:", result.errorMessage);
                runInAction(() => {
                    this.assignTaskError = true;
                });
            }
            return {
                success: result.success,
                taskId
            };
        })
            .catch((error) => {
            logger.error("[TasksStore] Error assigning task:", taskId, "Error:", error);
            runInAction(() => {
                this.assignTaskError = true;
            });
            // Re-throw the error or return a consistent error result object
            // For consistency with TaskAssignResult
            const errorResult = {
                success: false,
                taskId
            };
            return errorResult;
        });
    }
    assignMultipleTasks(taskIds) {
        return SERVICE.tasks
            .assignMultipleTasks(taskIds)
            .then((result) => {
            if (result.overallStatus === "allSucceeded") {
                logger.info("[TasksStore] All tasks assigned successfully:", taskIds);
                this.moveTasksFromQueuedToOpen(result.successfulTasks);
                runInAction(() => {
                    this.assignTaskError = false;
                });
            }
            else if (result.overallStatus === "partialFailure") {
                // Move successful tasks to open
                this.moveTasksFromQueuedToOpen(result.successfulTasks);
                logger.error("[TasksStore] Error assigning tasks:", taskIds, "Error:", result.failedTasks);
                runInAction(() => {
                    this.assignTaskError = true;
                });
            }
            else {
                // All failed
                logger.error("[TasksStore] Error assigning tasks:", taskIds, "Error:", result.failedTasks);
                runInAction(() => {
                    this.assignTaskError = true;
                });
            }
            return this.mapToMultipleTasksStoreAssignResult(result);
        })
            .catch((error) => {
            logger.error("[TasksStore] Unexpected error during multiple task assignment:", taskIds, "Error:", error);
            runInAction(() => {
                this.assignTaskError = true;
            });
            // Create failure result for all tasks
            const failedAssignments = taskIds.map((taskId) => ({
                success: false,
                taskId
            }));
            return {
                overallStatus: "allFailed",
                successfulTasks: [],
                failedTasks: failedAssignments
            };
        });
    }
    stopQueuedPolling() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.queuedPollingSubscriptionId) {
                try {
                    yield SERVICE.interactions.stopInteractionUpdates(this.queuedPollingSubscriptionId);
                    runInAction(() => {
                        this.queuedTasksError = false;
                    });
                    this.queuedPollingSubscriptionId = undefined;
                    this.queuedTasks.clear();
                }
                catch (error) {
                    logger.error("[TasksStore] Error stopping queued polling:", error);
                }
            }
        });
    }
    handleQueuedTasksCallback(data) {
        logger.debug("[TasksStore] Received queued tasks update, count:", data.length);
        const newTasks = data.map((taskDetail) => {
            return mapTaskDetailsToTask(taskDetail, this.userStore.isMaskSensitiveDataOrgSettingEnabled);
        });
        const removed = this.findRemovedTasks(newTasks);
        removed.forEach((task) => {
            this.queuedTasks.delete(task.id);
        });
        newTasks.forEach((task) => {
            const openTask = this.openTasks.get(task.id);
            if (openTask === null || openTask === void 0 ? void 0 : openTask.uiProcessing) {
                return;
            }
            this.queuedTasks.set(task.id, task);
        });
    }
    moveTasksFromQueuedToOpen(taskAssignResults) {
        runInAction(() => {
            taskAssignResults.forEach(({ taskId, contact }) => {
                if (!contact) {
                    logger.warn(`[TasksStore] _moveTasksFromQueuedToOpen called for taskId '${taskId}' but no contact object was provided (assignment might have failed for this task).`);
                    return;
                }
                if (this.queuedTasks.has(taskId)) {
                    this.openTasks.set(taskId, mapContactToTask(contact, this.userStore.isMaskSensitiveDataOrgSettingEnabled, true));
                    this.queuedTasks.delete(taskId);
                    logger.info("[TasksStore] Moved task from queued to open:", taskId);
                }
                else {
                    if (!this.openTasks.has(taskId)) {
                        this.openTasks.set(taskId, mapContactToTask(contact, this.userStore.isMaskSensitiveDataOrgSettingEnabled, true));
                        logger.info("[TasksStore] Task not in queued, added/updated in openTasks:", taskId);
                    }
                    else {
                        const existingOpenTask = this.openTasks.get(taskId);
                        if (existingOpenTask && !existingOpenTask.uiProcessing) {
                            this.openTasks.set(taskId, mapContactToTask(contact, this.userStore.isMaskSensitiveDataOrgSettingEnabled, true));
                            logger.info("[TasksStore] Updated existing open task with full contact details:", taskId);
                        }
                        else if (!existingOpenTask) {
                            this.openTasks.set(taskId, mapContactToTask(contact, this.userStore.isMaskSensitiveDataOrgSettingEnabled, true));
                        }
                    }
                    logger.warn("[TasksStore] Attempted to move task that was not found in queuedTasks:", taskId);
                }
            });
        });
    }
    mapToMultipleTasksStoreAssignResult(serviceResult) {
        return {
            overallStatus: serviceResult.overallStatus,
            successfulTasks: serviceResult.successfulTasks.map((task) => ({
                success: task.success,
                taskId: task.taskId
            })),
            failedTasks: serviceResult.failedTasks.map((task) => ({
                success: task.success,
                taskId: task.taskId,
                trackingId: task.trackingId
            }))
        };
    }
    findRemovedTasks(newTasks) {
        const newMap = new Map(newTasks.map((task) => [task.id, task]));
        const removed = [];
        for (const [id, task] of this.queuedTasks.entries()) {
            if (!newMap.has(id)) {
                removed.push(task);
            }
        }
        return removed;
    }
}
__decorate([
    observable
], TasksStore.prototype, "privateSelectedTask", void 0);
__decorate([
    computed
], TasksStore.prototype, "selectedTask", null);
__decorate([
    observable.ref
], TasksStore.prototype, "openTasks", void 0);
__decorate([
    observable.ref
], TasksStore.prototype, "queuedTasks", void 0);
__decorate([
    observable.ref
], TasksStore.prototype, "scheduledTasks", void 0);
__decorate([
    observable.ref
], TasksStore.prototype, "closedTasks", void 0);
__decorate([
    computed
], TasksStore.prototype, "openChatTaskCount", null);
__decorate([
    computed
], TasksStore.prototype, "openEmailTaskCount", null);
__decorate([
    computed
], TasksStore.prototype, "openTelephonyTaskCount", null);
__decorate([
    computed
], TasksStore.prototype, "openSocialTaskCount", null);
__decorate([
    computed
], TasksStore.prototype, "remainingChatTaskCount", null);
__decorate([
    computed
], TasksStore.prototype, "remainingEmailTaskCount", null);
__decorate([
    computed
], TasksStore.prototype, "remainingSocialTaskCount", null);
__decorate([
    observable
], TasksStore.prototype, "assignTaskError", void 0);
__decorate([
    observable
], TasksStore.prototype, "queuedTasksError", void 0);
__decorate([
    action
], TasksStore.prototype, "pollForQueuedTasks", null);
__decorate([
    action
], TasksStore.prototype, "fetchMoreQueuedTasks", null);
__decorate([
    action
], TasksStore.prototype, "setSelectedTask", null);
__decorate([
    action
], TasksStore.prototype, "assignTask", null);
__decorate([
    action
], TasksStore.prototype, "assignMultipleTasks", null);
__decorate([
    action
], TasksStore.prototype, "stopQueuedPolling", null);
__decorate([
    action
], TasksStore.prototype, "handleQueuedTasksCallback", null);
